import React from 'react';
import { X, ArrowRight, CheckCircle, XCircle, Trophy, Code, TrendingUp } from 'lucide-react';

interface CandidateComparisonProps {
  candidates: any[];
  isOpen: boolean;
  onClose: () => void;
}

const CandidateComparison: React.FC<CandidateComparisonProps> = ({
  candidates,
  isOpen,
  onClose,
}) => {
  if (!isOpen || candidates.length === 0) return null;

  // Ensure we have max 3 candidates
  const compareCandidates = candidates.slice(0, 3);

  const getRecommendationColor = (recommendation: string) => {
    switch (recommendation) {
      case 'STRONG RECOMMEND':
        return 'text-green-700 bg-green-100';
      case 'RECOMMEND':
        return 'text-blue-700 bg-blue-100';
      case 'NEUTRAL':
        return 'text-yellow-700 bg-yellow-100';
      case 'DO NOT RECOMMEND':
        return 'text-red-700 bg-red-100';
      default:
        return 'text-gray-700 bg-gray-100';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 bg-green-100';
    if (score >= 80) return 'text-blue-600 bg-blue-100';
    if (score >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getBestCandidate = () => {
    return compareCandidates.reduce((best, current) =>
      (current.totalScore || 0) > (best.totalScore || 0) ? current : best
    );
  };

  const best = getBestCandidate();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-red-600 to-orange-600 text-white p-6 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold flex items-center">
                <TrendingUp className="w-6 h-6 mr-2" />
                Candidate Comparison
              </h2>
              <p className="text-red-100 mt-1">
                Comparing {compareCandidates.length} candidates side-by-side
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Best Candidate Banner */}
          <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Trophy className="w-6 h-6 text-yellow-500 mr-3" />
                <div>
                  <div className="text-sm font-medium text-gray-600">Top Performer</div>
                  <div className="text-lg font-bold text-gray-900">{best.name}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-600">Total Score</div>
                <div className="text-3xl font-bold text-green-600">
                  {best.totalScore?.toFixed(1)}%
                </div>
              </div>
            </div>
          </div>

          {/* Comparison Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left p-4 font-semibold text-gray-700 border-b-2 border-gray-200">
                    Criteria
                  </th>
                  {compareCandidates.map((candidate, index) => (
                    <th
                      key={index}
                      className={`text-center p-4 font-semibold border-b-2 border-gray-200 ${
                        candidate.id === best.id
                          ? 'bg-green-50 text-green-800'
                          : 'text-gray-700'
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        <img
                          src={candidate.avatar}
                          alt={candidate.name}
                          className="w-12 h-12 rounded-full mb-2"
                        />
                        <div className="font-bold">{candidate.name}</div>
                        {candidate.id === best.id && (
                          <Trophy className="w-4 h-4 text-yellow-500 mt-1" />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Email */}
                <tr className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="p-4 font-medium text-gray-700">Email</td>
                  {compareCandidates.map((candidate, index) => (
                    <td key={index} className="p-4 text-center text-sm text-gray-600">
                      {candidate.email}
                    </td>
                  ))}
                </tr>

                {/* Position */}
                <tr className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="p-4 font-medium text-gray-700">Position</td>
                  {compareCandidates.map((candidate, index) => (
                    <td key={index} className="p-4 text-center text-sm text-gray-600">
                      {candidate.position}
                    </td>
                  ))}
                </tr>

                {/* Experience */}
                <tr className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="p-4 font-medium text-gray-700">Experience</td>
                  {compareCandidates.map((candidate, index) => (
                    <td key={index} className="p-4 text-center text-sm text-gray-600">
                      {candidate.experience || 'Not specified'}
                    </td>
                  ))}
                </tr>

                {/* MCQ Score */}
                <tr className="border-b border-gray-200 hover:bg-gray-50 bg-blue-50">
                  <td className="p-4 font-medium text-gray-700">MCQ Score (25)</td>
                  {compareCandidates.map((candidate, index) => (
                    <td key={index} className="p-4 text-center">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full font-bold ${getScoreColor(
                          (candidate.mcqScore / 25) * 100
                        )}`}
                      >
                        {candidate.mcqScore?.toFixed(1)} / 25
                      </span>
                    </td>
                  ))}
                </tr>

                {/* Interview Score */}
                <tr className="border-b border-gray-200 hover:bg-gray-50 bg-green-50">
                  <td className="p-4 font-medium text-gray-700">Interview Score (75)</td>
                  {compareCandidates.map((candidate, index) => (
                    <td key={index} className="p-4 text-center">
                      {candidate.hasCompletedInterview ? (
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full font-bold ${getScoreColor(
                            (candidate.interviewScore / 75) * 100
                          )}`}
                        >
                          {candidate.interviewScore?.toFixed(1)} / 75
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">Not completed</span>
                      )}
                    </td>
                  ))}
                </tr>

                {/* Total Score */}
                <tr className="border-b-2 border-gray-300 hover:bg-gray-50 bg-yellow-50">
                  <td className="p-4 font-bold text-gray-900">Total Score (100)</td>
                  {compareCandidates.map((candidate, index) => (
                    <td key={index} className="p-4 text-center">
                      <span
                        className={`inline-flex items-center px-4 py-2 rounded-full font-bold text-lg ${getScoreColor(
                          candidate.totalScore || 0
                        )}`}
                      >
                        {candidate.totalScore?.toFixed(1)}%
                      </span>
                    </td>
                  ))}
                </tr>

                {/* AI Recommendation */}
                <tr className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="p-4 font-medium text-gray-700">AI Recommendation</td>
                  {compareCandidates.map((candidate, index) => (
                    <td key={index} className="p-4 text-center">
                      <span
                        className={`inline-flex items-center px-3 py-1.5 rounded-full font-semibold text-sm ${getRecommendationColor(
                          candidate.recommendationForHiring || 'NEUTRAL'
                        )}`}
                      >
                        {candidate.recommendationForHiring === 'STRONG RECOMMEND' && (
                          <CheckCircle className="w-4 h-4 mr-1" />
                        )}
                        {candidate.recommendationForHiring === 'DO NOT RECOMMEND' && (
                          <XCircle className="w-4 h-4 mr-1" />
                        )}
                        {candidate.recommendationForHiring || 'NEUTRAL'}
                      </span>
                    </td>
                  ))}
                </tr>

                {/* Skills */}
                <tr className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="p-4 font-medium text-gray-700 align-top">Skills</td>
                  {compareCandidates.map((candidate, index) => (
                    <td key={index} className="p-4">
                      <div className="flex flex-wrap gap-1 justify-center">
                        {candidate.skills && candidate.skills.length > 0 ? (
                          candidate.skills.slice(0, 5).map((skill: string, idx: number) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full flex items-center"
                            >
                              <Code className="w-3 h-3 mr-1" />
                              {skill}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-400 text-sm">No skills listed</span>
                        )}
                        {candidate.skills && candidate.skills.length > 5 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                            +{candidate.skills.length - 5} more
                          </span>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>

                {/* Skills Match % (if available) */}
                {compareCandidates.some((c) => c.skillsMatchPercentage !== undefined) && (
                  <tr className="border-b border-gray-200 hover:bg-gray-50 bg-purple-50">
                    <td className="p-4 font-medium text-gray-700">Skills Match</td>
                    {compareCandidates.map((candidate, index) => (
                      <td key={index} className="p-4 text-center">
                        {candidate.skillsMatchPercentage !== undefined ? (
                          <span className="text-lg font-bold text-purple-600">
                            {candidate.skillsMatchPercentage}%
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">N/A</span>
                        )}
                      </td>
                    ))}
                  </tr>
                )}

                {/* Application Date */}
                <tr className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="p-4 font-medium text-gray-700">Applied</td>
                  {compareCandidates.map((candidate, index) => (
                    <td key={index} className="p-4 text-center text-sm text-gray-600">
                      {candidate.appliedDate}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Winner Summary */}
          <div className="mt-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
              <Trophy className="w-5 h-5 text-yellow-500 mr-2" />
              Recommendation
            </h3>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-gray-700">
                  Based on the comparison, <span className="font-bold">{best.name}</span> appears to
                  be the strongest candidate with a total score of{' '}
                  <span className="font-bold text-green-600">{best.totalScore?.toFixed(1)}%</span>
                  {best.recommendationForHiring === 'STRONG RECOMMEND' && (
                    <span> and a <span className="font-bold">STRONG RECOMMEND</span> rating from our AI.</span>
                  )}
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-400 ml-4" />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 rounded-b-xl">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
          >
            Close Comparison
          </button>
        </div>
      </div>
    </div>
  );
};

export default CandidateComparison;
